import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { Auftrag, CustomerRef, SpontanEinsatzCreate, StammkundeWorker } from '../../../Models/stammkunde.model';

export interface SpontanEinsatzFormData {
  customers: CustomerRef[];
  defaultCustomerId?: number;
}

/**
 * Dialog, mit dem der Vorarbeiter selbst einen Spontan-Einsatz anlegt. Er teilt sich
 * selbst ein; ohne gewählten Auftrag landet der Einsatz im Sammel-Auftrag des Kunden.
 */
@Component({
  selector: 'app-spontan-einsatz-form',
  templateUrl: './spontan-einsatz-form.component.html',
  styleUrls: ['./spontan-einsatz-form.component.css', '../stammkunden-theme.css']
})
export class SpontanEinsatzFormComponent implements OnInit {
  customerId: number | null = null;
  auftragId: number | null = null;
  auftraege: Auftrag[] = [];
  workers: StammkundeWorker[] = [];
  workerIds: number[] = [];

  title = '';
  startDate = '';
  startTime = '07:00';
  endTime = '16:00';
  location = '';
  notes = '';

  loadingContext = false;
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<SpontanEinsatzFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SpontanEinsatzFormData,
    private stammkundeService: StammkundeService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.startDate = this.toDateInput(new Date());
    const preset = this.data.defaultCustomerId;
    if (preset && this.data.customers.some(c => c.id === preset)) {
      this.customerId = preset;
      this.onCustomerChange();
    } else if (this.data.customers.length === 1) {
      this.customerId = this.data.customers[0].id;
      this.onCustomerChange();
    }
  }

  onCustomerChange(): void {
    this.auftragId = null;
    this.workerIds = [];
    this.auftraege = [];
    this.workers = [];
    if (this.customerId == null) { return; }

    this.loadingContext = true;
    forkJoin({
      auftraege: this.stammkundeService.getAuftraege(this.customerId),
      workers: this.stammkundeService.getWorkers(this.customerId)
    }).subscribe({
      next: res => {
        // Sammel-Auftrag nicht zur Auswahl anbieten – dort landet der Einsatz ohnehin.
        this.auftraege = (res.auftraege ?? []).filter(a => a.title !== 'Spontane Einsätze');
        this.workers = res.workers ?? [];
        this.loadingContext = false;
      },
      error: () => { this.loadingContext = false; }
    });
  }

  get valid(): boolean {
    return this.customerId != null && !!this.startDate && !!this.startTime && !!this.endTime
      && this.endTime > this.startTime;
  }

  setTimes(start: string, end: string): void {
    this.startTime = start;
    this.endTime = end;
  }

  isTimes(start: string, end: string): boolean {
    return this.startTime === start && this.endTime === end;
  }

  save(): void {
    if (!this.valid || this.saving || this.customerId == null) { return; }
    this.saving = true;

    const payload: SpontanEinsatzCreate = {
      customerId: this.customerId,
      auftragId: this.auftragId ?? undefined,
      title: this.title.trim() || undefined,
      start: `${this.startDate}T${this.startTime}:00`,
      end: `${this.startDate}T${this.endTime}:00`,
      location: this.location.trim() || undefined,
      notes: this.notes.trim() || undefined,
      workerIds: this.workerIds
    };

    this.stammkundeService.createSpontanEinsatz(payload).subscribe({
      next: result => {
        this.saving = false;
        this.dialogRef.close(result);
      },
      error: err => {
        this.saving = false;
        const message = (typeof err?.error === 'string' && err.error)
          || err?.error?.message
          || 'Spontan-Einsatz konnte nicht angelegt werden.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private toDateInput(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
